import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { askAI } from '../services/aiService';
import { Mic, MicOff, Send, X, Volume2, VolumeX, MessageCircle, Loader2, Minimize2, Maximize2 } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

const SYSTEM_INSTRUCTION = `You are "Pharmelo AI," a friendly and knowledgeable medical assistant for the Pharmelo platform in India. Keep responses concise, helpful, and focused strictly on the Pharmelo platform and pharmacy/health topics.

STRICT BOUNDARY RULES:
- ONLY answer questions directly related to Pharmelo's features, services, and platform. Pharmelo's core features are: ordering medicines, booking doctor appointments, pharmacy partnership/ownership, smart pharmacy services, prescription reading, and community/waitlist programs.
- NEVER provide code snippets, HTML/CSS/JS templates, website development help, tutorials, or any technical programming content.
- If a request is unrelated to Pharmelo, pharmacy, or health care, politely decline and redirect back to Pharmelo-related topics.
- Never say "I'm not a web developer" or offer to build code. Instead, say "That's outside what I can help with — I'm here for everything Pharmelo, medicines, or health-related queries!"

FORMATTING RULES:
- Use simple HTML-style jump links when referencing pages or features: [Plans](/plans), [About Us](/about), [Roadmap](/roadmap), [Partners](/partners), [Partner Form](/partner-form).
- Surround only the clickable phrase in brackets with the internal URL in parentheses.
- Never include raw URLs like https://pharmelo.in. Always use the jump link format.
- Sound natural and conversational.`;

const FloatingChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'Hello! I\'m Pharmelo AI. Ask me about medicines, health, or book a doctor appointment.' }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechErrorCount, setSpeechErrorCount] = useState(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<SpeechSynthesis | null>(null);
  const botMsgIdRef = useRef<number | null>(null);
  const pendingSpeakRef = useRef<string | null>(null);

  const parseBotContent = (text: string) => {
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    let html = escaped
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-emerald-700 underline break-all">$1</a>');
    return html;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      audioContextRef.current = window.speechSynthesis;
      const flushPending = () => {
        if (!pendingSpeakRef.current || !audioContextRef.current) return;
        const text = pendingSpeakRef.current;
        pendingSpeakRef.current = null;
        audioContextRef.current.cancel();
        const voice = getPreferredVoice();
        if (!voice) {
          pendingSpeakRef.current = text;
          return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.85;
        utterance.pitch = 0.95;
        utterance.volume = 0.9;
        utterance.voice = voice;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => {
          setIsSpeaking(false);
          pendingSpeakRef.current = null;
        };
        utterance.onerror = () => {
          setIsSpeaking(false);
          pendingSpeakRef.current = null;
        };
        audioContextRef.current.speak(utterance);
      };
      window.speechSynthesis.addEventListener('voiceschanged', flushPending);
      flushPending();
      return () => {
        window.speechSynthesis.removeEventListener('voiceschanged', flushPending);
      };
    }
  }, []);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.cancel();
      }
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
    };
  }, []);

  const toggleChat = () => setIsOpen(!isOpen);

  const stopSpeaking = () => {
    if (audioContextRef.current) {
      audioContextRef.current.cancel();
    }
    setIsSpeaking(false);
    pendingSpeakRef.current = null;
  };

  const getPreferredVoice = () => {
    if (!audioContextRef.current) return null;
    const voices = audioContextRef.current.getVoices();
    if (voices.length === 0) return null;
    return (
      voices.find(v => v.lang.startsWith('hi') && v.name.includes('Google')) ||
      voices.find(v => v.lang.startsWith('hi')) ||
      voices.find(v => v.lang.startsWith('en') && v.name.includes('Google')) ||
      voices.find(v => v.lang.startsWith('en-') && v.localService) ||
      voices.find(v => v.lang.startsWith('en')) ||
      voices[0]
    );
  };

  const speakText = (text: string) => {
    if (!audioContextRef.current) return;
    stopSpeaking();
    const cleanText = text
      .replace(/https?:\/\/[^\s]+/gi, 'Pharmelo')
      .replace(/[#*_~`>]/g, '')
      .replace(/\n+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!cleanText) return;

    const trySpeak = () => {
      const voice = getPreferredVoice();
      if (!voice) {
        pendingSpeakRef.current = cleanText;
        return;
      }
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.rate = 0.85;
      utterance.pitch = 0.95;
      utterance.volume = 0.9;
      utterance.voice = voice;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => {
        setIsSpeaking(false);
        pendingSpeakRef.current = null;
        if (isVoiceMode) {
          if (recognitionRef.current && !isRecording) {
            setTimeout(() => {
              try {
                recognitionRef.current.start();
              } catch (e) {}
            }, 350);
          }
        }
      };
      utterance.onerror = () => {
        setIsSpeaking(false);
        pendingSpeakRef.current = null;
      };

      audioContextRef.current!.speak(utterance);
    };

    trySpeak();
  };

  const handleSend = async () => {
    if (!inputText.trim() || isTyping) return;
    const text = inputText.trim();
    setInputText('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setIsTyping(true);

    try {
      let replyText = '';
      botMsgIdRef.current = null;
      
      await askAI(text, SYSTEM_INSTRUCTION, (chunk) => {
        replyText += chunk;
        
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          
          if (last.role === 'assistant' && botMsgIdRef.current !== null) {
            return [...updated.slice(0, -1), { ...last, content: replyText }];
          } else {
            botMsgIdRef.current = Date.now();
            return [...updated, { role: 'assistant', content: replyText }];
          }
        });
      });

      const lastBotMsg = [...messages, { role: 'user', content: text }]
        .filter(m => m.role === 'assistant')
        .pop();
      if (lastBotMsg && replyText && isVoiceMode) {
        speakText(replyText);
      }
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I\'m having trouble connecting right now. Please try again.' }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      try { recognitionRef.current?.stop(); } catch (e) {}
      setIsRecording(false);
      return;
    }

    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Voice input isn\'t supported in this browser. Please switch to Chrome or Edge.' }]);
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-IN';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsRecording(true);
    recognition.onresult = (event: any) => {
      const transcript = (event.results[0]?.[0]?.transcript || '').trim();
      if (!transcript) return;
      setSpeechErrorCount(0);
      setInputText(transcript);
      if (isVoiceMode) {
        setTimeout(() => handleSendWithInput(transcript), 100);
      }
    };
    recognition.onerror = (event: any) => {
      const err = event?.error || 'unknown-error';
      setIsRecording(false);
      if (err === 'not-allowed') {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Microphone permission is blocked. Please allow mic access in your browser settings and try again.' }]);
      } else if (err === 'no-speech') {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Didn\'t catch that. Tap the mic and try speaking again.' }]);
      } else if (err === 'network') {
        const next = speechErrorCount + 1;
        setSpeechErrorCount(next);
        if (next >= 2) {
          setIsVoiceMode(false);
          setMessages(prev => [...prev, { role: 'assistant', content: 'Voice service is currently unavailable. I\'ve switched off voice mode — you can keep typing here.' }]);
        }
      } else if (err !== 'aborted') {
        setMessages(prev => [...prev, { role: 'assistant', content: `Voice issue (${err}). You can type instead.` }]);
      }
    };
    recognition.onend = () => setIsRecording(false);

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (err: any) {
      setIsRecording(false);
      setMessages(prev => [...prev, { role: 'assistant', content: `Couldn\'t start voice input: ${err?.message || err}` }]);
    }
  };

  const handleSendWithInput = (text: string) => {
    setInputText(text);
    setTimeout(() => handleSend(), 100);
  };

  const handleReplayAudio = (text: string) => {
    speakText(text);
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed bottom-6 right-6 z-[9999]">
      {isOpen && (
        <div className="mb-4 w-[360px] max-w-[calc(100vw-3rem)] bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 flex flex-col overflow-hidden animate-fade-in"
             style={{ animation: 'fade-in 0.3s ease-out forwards' }}>
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle size={18} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Pharmelo AI</h3>
                <p className="text-[10px] text-emerald-100 flex items-center gap-1">
                  Online
                  {isVoiceMode && <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse ml-1" title="Voice mode active" />}
                  {isSpeaking && <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded-full ml-1">Speaking...</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsVoiceMode(!isVoiceMode)}
                className={`p-2 rounded-full transition-all ${isVoiceMode ? 'bg-white/20 text-white' : 'text-emerald-100 hover:text-white'}`}
                title={isVoiceMode ? 'Disable Voice Mode' : 'Enable Voice Mode'}
              >
                {isVoiceMode || isSpeaking ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
              <button onClick={toggleChat} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                <Minimize2 size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px] min-h-[250px] no-scrollbar bg-slate-50/50">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-emerald-600 text-white rounded-br-none'
                      : 'bg-white/90 text-slate-800 rounded-bl-none border border-slate-100'
                  }`}
                 >
                   {msg.role === 'assistant' ? (
                     <div dangerouslySetInnerHTML={{ __html: parseBotContent(msg.content) }} className="text-sm leading-relaxed" />
                   ) : (
                     msg.content
                   )}
                   {msg.role === 'assistant' && (
                    <div className="mt-1.5 flex justify-end">
                      <button
                        onClick={() => handleReplayAudio(msg.content)}
                        disabled={isSpeaking}
                        className="text-emerald-600 hover:text-emerald-700 p-1 rounded-full hover:bg-emerald-50 transition-colors disabled:opacity-40"
                        title="Read aloud"
                      >
                        <Volume2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white/90 rounded-2xl rounded-bl-none px-4 py-3 border border-slate-100 shadow-sm">
                  <Loader2 size={16} className="text-emerald-600 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-100 bg-white/60 shrink-0">
            {isRecording && (
              <div className="mb-2 flex items-center gap-2 text-[10px] font-bold text-red-500 uppercase tracking-wider animate-pulse">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
                Listening...
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={handleMicClick}
                className={`p-2.5 rounded-full transition-all ${
                  isRecording
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/40 animate-pulse'
                    : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                }`}
                title={isRecording ? 'Stop recording' : 'Voice input'}
              >
                {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type a message..."
                className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              />
              <button
                onClick={handleSend}
                disabled={!inputText.trim() || isTyping}
                className="p-2.5 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-emerald-600/20 active:scale-95"
              >
                <Send size={16} />
              </button>
            </div>
          </div>

          <style>{`
            .no-scrollbar::-webkit-scrollbar { display: none; }
            .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          `}</style>
        </div>
      )}

      {/* Floating Toggle Button */}
      <button
        onClick={toggleChat}
        className="w-14 h-14 bg-gradient-to-tr from-emerald-600 to-emerald-500 rounded-full shadow-2xl shadow-emerald-600/40 flex items-center justify-center text-white hover:scale-110 active:scale-95 transition-all border-4 border-white/20 backdrop-blur-sm"
      >
        {isOpen ? <Minimize2 size={22} /> : <MessageCircle size={24} />}
      </button>
    </div>,
    document.body
  );
};

export default FloatingChatbot;
